import { useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  summaryFraction,
  useTableQuery,
  type TableProps,
  type TableQueryParams,
} from "@/v2-backoffice-client/components/table"
import { useSearchParams } from "@/v2-backoffice-client/utils"
import { useTenantQuery } from "@/v2-backoffice-client/tenant/tenant.query"
import type { TenantQuery } from "@/v2-backoffice-client/tenant/__types__/tenant.query"
import { DotText } from "@/v2-backoffice-client/components/dot"
import TenantPrivacyPresetBadge from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyPresetBadge"
import { PRIVACY_POLICY_LABEL } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.constants"
import { privacyPolicyLabel } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.utils"
import TenantRemoveOrRestoreManyButton from "@/v2-backoffice-client/tenant/TenantRemoveOrRestoreManyButton"
import TenantMigrateManyButton from "@/v2-backoffice-client/tenant/migration/many/TenantMigrateManyButton"
import MigrationProgress from "@/v2-backoffice-client/tenant/migration/MigrationProgress"
import TenantCustomerAddManySelector from "@/v2-backoffice-client/tenant/customer/add/many/TenantCustomerAddManySelector"
import TenantTableRowToolbar from "@/v2-backoffice-client/tenant/TenantTableRowToolbar"
import TenantCustomerAddSelector from "@/v2-backoffice-client/tenant/customer/add/TenantCustomerAddSelector"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import { buildScopeUsageColumns } from "@/v2-backoffice-client/usage/usage.columns"
import { IS_DELETED_COLUMN } from "@/v2-backoffice-client/tenant/tenant.constants"

const PAGE_SIZE = 100
const ACTIVE_STATUS_CODE = 0
const DELETED_STATUS_CODE = 1

// The list's row: TenantFragment plus the personal-data summary the list
// query selects on top of it (see tenant.query.gql for why it is not in the
// fragment).
type TenantListItem = TenantQuery["getTenants"]["items"][number]

/**
 * The status pill's server filter for the table's FIRST query, resolved
 * from the same URL param Table.StatusFilter persists to — in exactly the
 * shape buildQueryParams emits once the pill mounts, so Apollo sees
 * deep-equal variables and never fires a wasted unfiltered query on load.
 */
function buildTenantStatusParams(statusParam: string | null): TableQueryParams {
  const eq =
    statusParam === String(DELETED_STATUS_CODE)
      ? String(DELETED_STATUS_CODE)
      : String(ACTIVE_STATUS_CODE)
  return { filters: [{ column: IS_DELETED_COLUMN, eq }] }
}

const COLUMN_SIZING = {
  ExternalCustomerId: 150,
  UserCount: 70,
  ActiveUserCount: 80,
  InActiveUserCount: 80,
  InvitationCount: 80,
  IsDeleted: 70,
}

/** Opens the team panel straight on the tab where the policy is edited. */
function accessControlTabUrl(tenantId: number) {
  return `?v=tenant:${tenantId}&tenant-view-tabs-tab=access-control`
}

function createTenantColumns() {
  return createColumns<TenantListItem>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns: {
      Name: {
        id: "Name",
        label: "Name",
        filter: { type: "text", placeholder: "Name..." },
        to: ({ row }) => `?v=tenant:${row.original.Id}`,
      },
      ExternalCustomerId: {
        id: "ExternalCustomerId",
        label: "Hubspot Id",
        defaultHidden: true,
        filter: { type: "text", placeholder: "HubSpot id..." },
        to: ({ value }) => {
          if (!value) return
          return `${HUBSPOT_BASE_URL}/${value}`
        },
      },
      // The Fenerum sibling of Hubspot Id: the owning customer's linked
      // billing account, linking to its Fenerum page.
      BillingAccountUuid: {
        id: "BillingAccountUuid",
        label: "Fenerum Id",
        defaultHidden: true,
        enableSorting: false,
        to: ({ value }) => {
          if (!value) return
          return `${FENERUM_ACCOUNT_BASE_URL}/${value}/`
        },
      },
      CustomerName: {
        id: "CustomerName",
        label: "Customer",
        enableGrouping: true,
        filter: { type: "text", placeholder: "Filter by customer..." },
        renderCell({ row }) {
          if (row.original.CustomerName) return row.original.CustomerName
          return (
            <TenantCustomerAddSelector
              tenantId={row.original.Id}
              externalCustomerId={row.original.ExternalCustomerId}
              variant="ghost"
              size="tiny"
              label="Update customer"
            />
          )
        },
        to: ({ row }) => {
          if (!row.original.ExternalCustomerId) return
          return `?v=customer:${row.original.ExternalCustomerId}`
        },
      },
      UserCount: {
        id: "UserCount",
        label: "Total Users",
        filter: { type: "number-range" },
      },
      ActiveUserCount: {
        id: "ActiveUserCount",
        label: "Active Users",
        filter: { type: "number-range" },
      },
      InactiveUserCount: {
        id: "InactiveUserCount",
        label: "Inactive Users",
        filter: { type: "number-range" },
      },
      // The migration target group (FUS-927): members with v2 evidence.
      // The wave selection filter: V2 Users > 0 → select all → Fusion.
      MigrationV2Users: {
        id: "MigrationV2Users",
        label: "V2 Users",
        defaultSize: 90,
        enableSorting: false,
        filter: { type: "number-range" },
      },
      // Fusion-login migration progress (FUS-927). The id doubles as the
      // server sort key: the proc sorts by activated/total ratio.
      MigrationProgress: {
        id: "MigrationProgress",
        label: "Login Migration",
        defaultSize: 160,
        renderCell({ row }) {
          return <MigrationProgress rollup={row.original} />
        },
        exportValue({ row }) {
          return `${row.original.MigrationActivated ?? 0}/${row.original.MigrationTotal ?? 0}`
        },
        // Both numbers the bar shows, so the summary adds up activated AND
        // members ("48/152") and reads the share migrated; a team without
        // members (no bar) counts as empty.
        summaryValue({ row }) {
          return summaryFraction(
            row.original.MigrationActivated,
            row.original.MigrationTotal,
          )
        },
      },
      // The team's personal-data policy (FUS-1184): the preset its
      // effective policy matches, or Custom. The value lives in v2-core-db
      // while the list is sorted and filtered in v2-userland-db, so neither
      // can run server-side — grouping over the loaded rows is the overview
      // ("which teams still hide everything"), and the cell deep-links to
      // the tab where the policy is changed.
      PrivacyPolicy: {
        id: "PrivacyPolicy",
        label: PRIVACY_POLICY_LABEL,
        description:
          "Which personal-data fields the team's members can see, per data source: the preset the policy matches, or Custom. Set in the team's Access Control tab.",
        defaultSize: 120,
        enableSorting: false,
        enableGrouping: true,
        getValue: (row) => privacyPolicyLabel(row.PrivateDataVisibilitySummary),
        renderCell({ row }) {
          return (
            <TenantPrivacyPresetBadge
              size="xs"
              summary={row.original.PrivateDataVisibilitySummary}
            />
          )
        },
        to: ({ row }) => accessControlTabUrl(row.original.Id),
      },
      // Deep link into the team panel's Activity tab so staff can dig
      // into the members behind the aggregates.
      ...buildScopeUsageColumns<TenantListItem>({
        buildTo: (row) => `?v=tenant:${row.Id}&tenant-view-tabs-tab=sign-ins`,
      }),
      InvitationCount: {
        id: "InvitationCount",
        label: "Invitations",
        defaultHidden: true,
        defaultSize: 80,
      },
      IsDeleted: {
        id: "IsDeleted",
        label: "Deleted",
        defaultHidden: true,
        enableGrouping: true,
        renderCell({ row }) {
          return (
            <DotText
              variant={row.original.IsDeleted ? "destructive" : "success-1"}
            >
              {row.original.IsDeleted ? "Yes" : "No"}
            </DotText>
          )
        },
        exportValue({ row }) {
          return row.original.IsDeleted ? "Yes" : "No"
        },
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type TenantTableProps = Omit<
  TableProps<TenantListItem>,
  "data" | "columns"
> & {
  isLoading?: boolean
  activeCount?: number
  removedCount?: number
}

function TenantTable(props: TenantTableProps) {
  const { className, isLoading, activeCount, removedCount, ...tableProps } =
    props

  const opener = useAppViewSheetOpener()
  const [isViewingDeleted, setIsViewingDeleted] = useState(false)
  const [searchParams] = useSearchParams()
  const columns = useMemo(() => createTenantColumns(), [])

  const {
    items,
    pageInfo,
    loading,
    error,
    refetch,
    onFetchMore,
    onQueryChange,
  } = useTableQuery({
    pageSize: PAGE_SIZE,
    useQuery: useTenantQuery,
    select(data) {
      return data.getTenants
    },
    initialParams: buildTenantStatusParams(searchParams.get("status")),
  })

  return (
    <Table
      className={cn("tenant-table", className)}
      tableId="tenant-table"
      data={items}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      enableGrouping
      enableRowSelection
      manualSorting
      manualFiltering
      onQueryChange={onQueryChange}
      getHighlightedRowId={(row) => {
        return opener.getIsActive("tenant", row.original.Id)
      }}
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.StatusFilter
          column={IS_DELETED_COLUMN}
          defaultValue={ACTIVE_STATUS_CODE}
          onChange={(value) =>
            setIsViewingDeleted(value === DELETED_STATUS_CODE)
          }
        >
          <Table.StatusFilter.Option
            value={null}
            count={
              activeCount !== undefined && removedCount !== undefined
                ? activeCount + removedCount
                : undefined
            }
          >
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="success"
            value={ACTIVE_STATUS_CODE}
            count={activeCount}
          >
            Active
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="destructive"
            value={DELETED_STATUS_CODE}
            count={removedCount}
          >
            Removed
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />

        <Table.Search column="Name" placeholder="Search by name..." />
        <Table.ToolbarFilters />

        <Separator orientation="vertical" />
        <Table.ToolbarMenu onRefresh={refetch} exportFileName="tenants" />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<TenantListItem> column="Name">
        {(tenant) => <TenantTableRowToolbar tenant={tenant} />}
      </Table.RowToolbar>

      <Table.Content
        countLabel="teams"
        isLoading={isLoading || loading}
        error={error}
        pageInfo={pageInfo}
        onFetchMore={onFetchMore}
      />

      <Table.SelectionToolbar<TenantListItem>>
        {(tenants, reset) => {
          const tenantIds = tenants.map((t) => t.Id)

          return (
            <>
              <Table.ExportSelectionButton
                fileName="tenants"
                selectionCount={tenants.length}
              />
              <TenantCustomerAddManySelector
                isStacked
                variant="ghost"
                tenantIds={tenantIds}
                onCompleted={reset}
                content="Update the customer the selected teams belong to."
              >
                Customer
              </TenantCustomerAddManySelector>
              <TenantMigrateManyButton tenants={tenants} onCompleted={reset} />
              <TenantRemoveOrRestoreManyButton
                isStacked
                tenantIds={tenantIds}
                isDeleted={isViewingDeleted}
                onCompleted={reset}
              >
                {isViewingDeleted ? "Restore" : "Remove"}
              </TenantRemoveOrRestoreManyButton>
            </>
          )
        }}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default TenantTable
